import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { keys } from '../../api/keys'
import type { components } from '../../api/generated/schema'
import { SAVE_COPY } from './useSaveRecurring'
import { CONFLICT_COPY } from './useLifecycle'
import { billKind, patchRecurring, RecurringWriteError, type BillWriteKind } from './recurringApi'
import type { Bill } from './recurringWire'

type UpdateBody = components['schemas']['RecurringUpdate']

/** NEW COPY: the form's "Check the name, amount and date" names fields this panel does not have. */
const INVALID_COPY = 'Check the reminder and amount, then try again.'

/** Why a settings save did not happen. `message` is the sentence the panel shows as it stands. */
export class SettingsSaveError extends Error {
  readonly kind: BillWriteKind
  constructor(kind: BillWriteKind, cause?: unknown) {
    // S13's own wording: unlike the form, this panel re-seeds from the latest.
    super(kind === 'conflict' ? CONFLICT_COPY : kind === 'invalid' ? INVALID_COPY : SAVE_COPY[kind], { cause })
    this.name = 'SettingsSaveError'
    this.kind = kind
  }
}

async function send(id: string, body: UpdateBody): Promise<Bill> {
  try {
    return await patchRecurring(id, body)
  } catch (e) {
    throw e instanceof RecurringWriteError ? new SettingsSaveError(billKind(e), e.cause) : new SettingsSaveError('failed', e)
  }
}

/**
 * One PATCH for the whole panel (S13, Review Focus 4): the body already carries
 * the `version` the user last saw, and a 409 is never retried here.
 *
 * Shares `keys.recurring.save('edit', id)` with the edit form, so `saving` is
 * true whichever screen sent the write. A second `save` while one is out
 * returns the SAME promise — a double click sends one request. The settle
 * invalidates the `['recurring']` root, which is also the refetch a 409 needs:
 * the panel re-seeds when the latest bill arrives.
 */
export function useSaveBillSettings(bill: Bill): { save: (body: UpdateBody) => Promise<Bill>; saving: boolean } {
  const client = useQueryClient()
  const inFlight = useRef<Promise<Bill> | null>(null)
  const mutationKey = keys.recurring.save('edit', bill.id)
  const saving = useMutationState({ filters: { mutationKey, exact: true, status: 'pending' } }).length > 0

  const { mutateAsync } = useMutation<Bill, Error, UpdateBody>({
    mutationKey,
    mutationFn: (body) => send(bill.id, body),
    // Not awaited: the caller settles on the server's answer.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.recurring.all() })
    },
  })

  const save = useCallback(
    (body: UpdateBody): Promise<Bill> => {
      if (inFlight.current !== null) return inFlight.current
      const run = mutateAsync(body).finally(() => {
        inFlight.current = null
      })
      inFlight.current = run
      return run
    },
    [mutateAsync],
  )
  return { save, saving }
}
